import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { InventoryFilter } from 'src/app/inventory/services/inventory-filter.service';
import {
  AssetCategory,
  assetCategoryDecoder,
} from 'src/app/models/asset-category';
import { EntityService, SortFilter } from 'src/app/util/entity.service';
import {
  HttpServiceBuilder,
  HttpServiceConfig,
  PaginationOptions,
} from 'src/app/util/http.service';

type AssetCategorySortColumn = 'asset_category_name' | 'asset_count';

export type AssetCategorySortFilter = SortFilter<AssetCategorySortColumn>;

export interface AssetCategoriesFilter
  extends InventoryFilter<AssetCategorySortColumn> {
  deleted?: boolean;
  homeLocationId?: string;
  summary?: boolean;
  categoryIds?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class AssetCategoriesService extends EntityService<
  AssetCategory,
  AssetCategoriesFilter
> {
  constructor(httpServiceBuilder: HttpServiceBuilder<AssetCategory>) {
    super(httpServiceBuilder, 'asset_categories', assetCategoryDecoder);
  }

  getEntityListConfig(
    filter: AssetCategoriesFilter
  ): HttpServiceConfig<AssetCategory, AssetCategory> {
    const config = { ...this.httpService.getConfig() };
    let route = config.route;
    if (filter.summary) {
      route = 'assets/uncataloged_summary';
    } else if (filter.cataloged) {
      route = filter.owned
        ? 'asset_categories/catalog_owned'
        : 'asset_categories/catalog';
    } else if (filter.uncataloged) {
      route = filter.owned
        ? 'asset_categories/noncatalog_owned'
        : 'asset_categories/noncatalog';
    }
    return {
      ...config,
      route,
    };
  }

  getEntityListQuery(filter: AssetCategoriesFilter): {} {
    const query = {};

    if (filter.search) {
      query['search'] = filter.search;
    }
    if (filter.deleted === true) {
      query['deleted'] = true;
    }
    if (filter.homeLocationId) {
      query['home_location_id'] = filter.homeLocationId;
    }
    if (filter.categoryIds && filter.categoryIds.length > 0) {
      query['asset_category_id[]'] = filter.categoryIds;
    }
    if (filter.categories && filter.categories.length > 0) {
      query['asset_category_id[]'] = filter.categories;
    }

    if (filter.purchaseDateStart) {
      const endDate = filter.purchaseDateEnd || new Date();
      query['purchase_date[]'] = [filter.purchaseDateStart, endDate];
    }

    if (filter.warrantyDateStart) {
      const endDate = filter.warrantyDateEnd || new Date();
      query['warranty_date[]'] = [filter.warrantyDateStart, endDate];
    }

    if (typeof filter.tracked === 'boolean') {
      query['tracked'] = filter.tracked;
    }

    if (filter.sort) {
      Object.entries(filter.sort).forEach(([key, value]) => {
        if (value) {
          query[`sort[${key}]`] = value;
        }
      });
    }

    if (filter.location) {
      query['location_id'] = filter.location;
    }

    if (filter.homeLocation) {
      query['home_location_id'] = filter.homeLocation;
    }

    return query;
  }

  getAssetGroups(
    filter: AssetCategoriesFilter,
    pageOpts: PaginationOptions
  ): Observable<Record<string, AssetCategory[]>> {
    return this.getEntityList(filter, pageOpts).pipe(
      map((entityList) =>
        entityList.entities.reduce((prev, category) => {
          const group = category.asset_group;
          return {
            ...prev,
            [group]: [...(prev[group] || []), category],
          };
        }, {})
      )
    );
  }
}
